import axios from 'axios';
import { format } from 'date-fns';

import { ApiResponse } from '../../models/api.response.model';
import { Error } from '../../models/error.model';
import { Message } from '../../models/message.model';
import { Company } from '../../models/company.model';
import { ProviderOrder } from '../../models/provider.order.model';
import { CompanyProviderOrders } from '../../models/company.provider.orders.model';
import { extractBase64 } from '../../scripts/extract.base.64.script';
import { authToken } from '../../scripts/authentication.script';

export class ProviderOrderDA {
  static apiResponse: ApiResponse

  public static async getProviderOrders(
    id_company: string,
    interval: string,
    start_date: string,
    end_date: string,
    contact_id: string,
    treasury_id: string,
    status: string,
    sort_field: string,
    sort_mode: string,
    group_by: string,
    page: number = 1,
    limit: number = 20,
    search: string = '',
    all: boolean = false
  ): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      let companyProviderOrderList: CompanyProviderOrders | null = null
      let companyProviderOrderGroup: CompanyProviderOrders | null = null
      let error: Error | null = null

      await axios.get('../../../../../database/purchase.orders.list.data.json').then(response => { companyProviderOrderList = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/purchase.orders.group.data.json').then(response => { companyProviderOrderGroup = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/error.data.json').then(response => { error = response.data }).catch(error => { alert(error) })

      if (companyProviderOrderList!.id_company === id_company || companyProviderOrderGroup!.id_company === id_company) {
        if (group_by.length === 0) {
          companyProviderOrderList!.filter = { interval: interval, start_date: start_date, end_date: end_date, contact: contact_id, treasury: treasury_id, status: status, sort_field: sort_field, sort_mode: sort_mode, group_by: group_by }
        } else {
          companyProviderOrderGroup!.filter = { interval: interval, start_date: start_date, end_date: end_date, contact: contact_id, treasury: treasury_id, status: status, sort_field: sort_field, sort_mode: sort_mode, group_by: group_by }
        }

        this.apiResponse = {
          status: 200,
          message: 'Success',
          data: (group_by.length === 0) ? companyProviderOrderList : companyProviderOrderGroup
        }
      } else {
        this.apiResponse = {
          status: 400,
          message: 'Bad request',
          data: error
        }
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/provider-orders?interval=${interval}&start_date=${start_date}&end_date=${end_date}&id_contact=${contact_id}&treasury_account=${treasury_id}&status=${status}&sort_by=${sort_field}&sort_order=${sort_mode}&group_by=${group_by}${all ? '&all=true' : `&page=${page}&limit=${limit}`}${search ? `&search=${encodeURIComponent(search)}` : ''}`

      let config: any = {
        headers: { Authorization: authToken() }
      }

      await axios.get(url, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Error',
          data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
        }
      })
    }

    return this.apiResponse
  }

  public static async getProviderOrder(
    id_company: string,
    id_provider_order: string
  ): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      let providerOrder: ProviderOrder | null = null
      let error: Error | null = null

      await axios.get('../../../../../database/purchase.order.data.json').then(response => { providerOrder = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/error.data.json').then(response => { error = response.data }).catch(error => { alert(error) })

      if (providerOrder!.id === id_provider_order) {
        this.apiResponse = {
          status: 200,
          message: 'Success',
          data: providerOrder
        }
      } else {
        this.apiResponse = {
          status: 400,
          message: 'Bad request',
          data: error
        }
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/provider-orders/${id_provider_order}`

      let config: any = {
        headers: { Authorization: authToken() }
      }

      await axios.get(url, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response.status,
          message: error.response.statusText,
          data: error.response.data.error
        }
      })
    }

    return this.apiResponse
  }

  public static async postProviderOrder(
    id_company: string,
    provider: any,
    concerning: string,
    date: string,
    currency: any,
    foreign_currency: any,
    requirements: { type: string, description: string, contents: { detail: string, reference: string, quantity: string, price_unit: string, tax_included: boolean, tax_code: string, tax_rate: number, unit: string }[] }[],
    vouchers: { name: string, file: any }[],
    signatures: string[],
    company_detail: { uuid: string, name: string, logo: string, address: { street: string, number: string, additional: string, administrative_area: string, city: string, zip_code: string, country: string } },
    reference: string
  ): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      let company: Company | null = null
      let message: Message | null = null
      let error: Error | null = null

      await axios.get('../../../../../database/company.data.json').then(response => { company = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/message.data.json').then(response => { message = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/error.data.json').then(response => { error = response.data }).catch(error => { alert(error) })

      if (company!.id === id_company) {
        this.apiResponse = {
          status: 201,
          message: 'Success',
          data: message
        }
      } else {
        this.apiResponse = {
          status: 400,
          message: 'Bad request',
          data: error
        }
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/provider-orders`

      let config: any = {
        headers: { Authorization: authToken() }
      }

      let data: any = new FormData()
      data.append("concerning", concerning)
      data.append("provider", JSON.stringify(provider))
      data.append("created_date", date)
      data.append("reference", reference)
      data.append('currency', JSON.stringify(currency))
      data.append('foreign_currency', foreign_currency ? JSON.stringify(foreign_currency) : '')
      data.append('company', JSON.stringify(company_detail))

      requirements.forEach((item) => {
        data.append('requirements', JSON.stringify(item))
      })

      signatures.forEach((item) => {
        data.append('signatures', item)
      })

      vouchers.forEach((item) => {
        if (item.file instanceof File) {
          data.append("vouchers", item.file, item.name)
        } else {
          data.append("vouchers_url", item.file)
          data.append("vouchers_name", item.name)
        }
      })

      await axios.post(url, data, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response.status,
          message: error.response.statusText,
          data: error.response.data.error
        }
      })
    }

    return this.apiResponse
  }

  public static async postProviderOrderDraft(
    id_company: string,
    vouchers: { name: string, file: any }[],
    company_detail: { uuid: string, name: string, logo: string, address: { street: string, number: string, additional: string, administrative_area: string, city: string, zip_code: string, country: string } }
  ): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      let company: Company | null = null
      let providerOrder: ProviderOrder | null = null
      let error: Error | null = null

      await axios.get('../../../../../database/company.data.json').then(response => { company = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/purchase.order.draft.data.json').then(response => { providerOrder = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/error.data.json').then(response => { error = response.data }).catch(error => { alert(error) })

      if (company!.id === id_company) {
        let listVouchers = await Promise.all(
          vouchers.map(async (item) => {
            let file = await extractBase64(item.file) as { base: string }
            return {
              id: `voucher${(Math.floor(Math.random() * 1000)).toString()}`,
              name: item.name,
              size: item.name.length * 100,
              created_at: `${format((new Date()), `yyyy-MM-dd'T'HH:mm:ss.SSSXXX`)}`,
              file: file.base
            }
          })
        )

        providerOrder!.vouchers = listVouchers

        this.apiResponse = {
          status: 201,
          message: 'Success',
          data: providerOrder
        }
      } else {
        this.apiResponse = {
          status: 400,
          message: 'Bad request',
          data: error
        }
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/provider-orders/draft`

      let config: any = {
        headers: { Authorization: authToken() }
      }

      let data: any = new FormData();
      data.append('company', JSON.stringify(company_detail))

      vouchers.forEach((item) => {
        if (item.file instanceof File) {
          data.append("vouchers", item.file, item.name)
        } else {
          data.append("vouchers_url", item.file)
          data.append("vouchers_name", item.name)
        }
      })

      await axios.post(url, data, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response.status,
          message: error.response.statusText,
          data: error.response.data.error
        }
      })
    }

    return this.apiResponse
  }

  public static async putProviderOrder(
    id_company: string,
    id_provider_order: string,
    provider: any,
    concerning: string,
    date: string,
    currency: any,
    foreign_currency: any,
    requirements: { type: string, description: string, contents: { detail: string, reference: string, quantity: string, price_unit: string, tax_included: boolean, tax_code: string, tax_rate: number, unit: string }[] }[],
    vouchers: { name: string, file: any }[],
    signatures: string[]
  ): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      let providerOrder: ProviderOrder | null = null
      let message: Message | null = null
      let error: Error | null = null

      await axios.get('../../../../../database/purchase.order.data.json').then(response => { providerOrder = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/message.data.json').then(response => { message = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/error.data.json').then(response => { error = response.data }).catch(error => { alert(error) })

      if (providerOrder!.id === id_provider_order) {
        this.apiResponse = {
          status: 200,
          message: 'Success',
          data: message
        }
      } else {
        this.apiResponse = {
          status: 400,
          message: 'Bad request',
          data: error
        }
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/provider-orders/${id_provider_order}`

      let config: any = {
        headers: { Authorization: authToken() }
      }

      let data: any = new FormData()
      data.append("concerning", concerning)
      data.append("provider", JSON.stringify(provider))
      data.append('created_date', date)
      data.append('currency', JSON.stringify(currency))
      data.append('foreign_currency', foreign_currency ? JSON.stringify(foreign_currency) : '')

      requirements.forEach((item) => {
        data.append('requirements', JSON.stringify(item))
      })

      signatures.forEach((item) => {
        data.append('signatures', item)
      })

      vouchers.forEach((item) => {
        if (item.file instanceof File) {
          data.append("vouchers", item.file, item.name)
        } else {
          data.append("vouchers_url", item.file)
          data.append("vouchers_name", item.name)
        }
      })

      await axios.put(url, data, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response.status,
          message: error.response.statusText,
          data: error.response.data.error
        }
      })
    }

    return this.apiResponse
  }

  public static async patchProviderOrderStorageOperations(
    id_company: string,
    id_provider_order: string,
    id_storage_operation: string
  ): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      this.apiResponse = { status: 200, message: 'Success', data: null }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/provider-orders/${id_provider_order}/storage-operations`
      let config: any = { headers: { Authorization: authToken() } }
      let data: any = { id_storage_operation: id_storage_operation }

      await axios.patch(url, data, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Unknown error',
          data: error.response?.data?.error ?? error.message
        }
      })
    }

    return this.apiResponse
  }

  public static async putProviderOrderStatus(
    id_company: string,
    id_provider_order: string,
    new_status: string
  ): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      let providerOrder: ProviderOrder | null = null
      let error: Error | null = null

      await axios.get('../../../../../database/purchase.order.data.json').then(response => { providerOrder = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/error.data.json').then(response => { error = response.data }).catch(error => { alert(error) })

      if (providerOrder!.id === id_provider_order) {
        providerOrder!.status = new_status

        this.apiResponse = {
          status: 200,
          message: 'Success',
          data: providerOrder
        }
      } else {
        this.apiResponse = {
          status: 400,
          message: 'Bad request',
          data: error
        }
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/provider-orders/${id_provider_order}/status`

      let config: any = {
        headers: { Authorization: authToken() }
      }

      let data = {
        status: new_status
      }

      await axios.put(url, data, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response.status,
          message: error.response.statusText,
          data: error.response.data.error
        }
      })
    }
    return this.apiResponse
  }

  public static async attachVoucher(id_company: string, id_provider_order: string, body: { id_document_inbox: string, role?: string }): Promise<any> {
    try {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/provider-orders/${id_provider_order}/vouchers`
      let config: any = { headers: { Authorization: authToken() } }
      const response = await axios.post(url, body, config)
      return { status: response.status, data: response.data?.data ?? response.data }
    } catch (error: any) {
      return { status: error.response?.status ?? 500, data: error.response?.data ?? {} }
    }
  }

  public static async uploadVoucher(id_company: string, id_provider_order: string, file: File, role?: string): Promise<any> {
    try {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/provider-orders/${id_provider_order}/vouchers/upload`
      const formData = new FormData()
      formData.append('file', file)
      formData.append('source', 'web')
      if (role) formData.append('role', role)
      let config: any = { headers: { Authorization: authToken(), 'Content-Type': 'multipart/form-data' } }
      const response = await axios.post(url, formData, config)
      return { status: response.status, data: response.data?.data ?? response.data }
    } catch (error: any) {
      return { status: error.response?.status ?? 500, data: error.response?.data ?? {} }
    }
  }

  public static async deleteVoucher(id_company: string, id_provider_order: string, index: number): Promise<any> {
    try {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/provider-orders/${id_provider_order}/vouchers/${index}`
      let config: any = { headers: { Authorization: authToken() } }
      const response = await axios.delete(url, config)
      return { status: response.status, data: response.data?.data ?? response.data }
    } catch (error: any) {
      return { status: error.response?.status ?? 500, data: error.response?.data ?? {} }
    }
  }
}
